/**
 * O anexo da conversa: prévia na bolha e lightbox por cima de tudo.
 *
 * O anexo era um cartãozinho com a extensão em caixa alta — o atendimento da
 * casa é feito de áudio e foto, então a conversa mostrava vinte e seis retângulos
 * cinza dizendo "MP3". Aqui cada tipo aparece como ele é: imagem como imagem,
 * áudio com player, vídeo com player, PDF com prévia. O que não tem prévia
 * possível (contato, planilha) continua cartão, mas com download que funciona.
 *
 * O lightbox é um só para a tela inteira e mora num estado de módulo: a bolha e
 * a aba de anexos abrem o MESMO visor, com a lista completa da conversa, então
 * a seta continua andando de anexo em anexo mesmo tendo aberto pela bolha.
 *
 * Sobre a migração para o shadcn: quase toda medida daqui é CALCULADA — a cor do
 * player sai de `currentColor` (ele toca dentro da bolha branca e da bolha
 * preta), a moldura vem de `escuro`/`limpo`, e `raio`, `largura` e `teto` são
 * prop pública. Por isso o `style` continua onde continua: classe do Tailwind
 * não expressa um valor que só existe na chamada.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Attachment,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from "@/ui/attachment";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Spinner } from "@/ui/spinner";
import { dataHora, Icone, ICONES, mmss } from "../visual.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Rosto } from "./comuns.jsx";
import { EXTENSAO, familiaDoAnexo, lerVCard } from "../conversa.js";
import { CortinaLivre, Fechar } from "./comuns.jsx";
import LeitorDePdf from "./LeitorDePdf.jsx";
import { useNavigate } from "react-router";
import { useAcoes, usePode } from "../estado.jsx";

/* ------------------------------------------------------- prévia sob demanda */

/*
 * Abrir o caso da Angélica baixava 29 MB e travava dezesseis segundos.
 *
 * A conta, medida: 26 anexos, e cada linha da lista montava `<video>` e
 * `<audio>` com `preload="metadata"`. "Metadata" não é barato como o nome
 * promete — MP3 de taxa variável sem cabeçalho de duração e MP4 com o `moov` no
 * fim obrigam o navegador a ler o arquivo INTEIRO só para saber quantos minutos
 * tem. Dava 20 MB de vídeo mais 10 MB de áudio antes de alguém tocar em nada,
 * numa VM de 1 OCPU atrás de rede privada.
 *
 * Daqui para frente o `src` só entra no elemento quando ele chega perto da
 * tela. O cartaz, a onda semeada e o selo já existiam e não custam rede — quem
 * custa é o byte.
 */

/*
 * O `root` do observador é o container que ROLA, não a janela.
 *
 * A trilha rola dentro da coluna da conversa, os anexos dentro do painel e o
 * acervo dentro da vista. Com `root: null` a interseção até respeita o corte
 * desses containers (o cálculo apara o retângulo em cada ancestral), mas a
 * MARGEM de antecipação vale só na raiz — dentro da coluna o anexo só contaria
 * como "perto" quando já estivesse visível, e buscar no instante em que aparece
 * é justamente o piscar que a prévia sob demanda existe para evitar.
 *
 * É também por isto que nada aqui virou `ScrollArea`: esta função sobe a árvore
 * procurando quem tem `overflow-y`, e um viewport interno mudaria a resposta.
 */
export function raizDeRolagem(el) {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const y = getComputedStyle(p).overflowY;
    if (y === "auto" || y === "scroll") return p;
  }
  return null;
}

/* Um observador por container, compartilhado: um por anexo daria vinte e seis
   observadores recalculando interseção na mesma rolagem. */
const observadores = new Map();

export function paraObservar(raiz) {
  let reg = observadores.get(raiz);
  if (!reg) {
    const alvos = new Map();
    const obs = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          if (!e.isIntersecting) continue;
          const avisar = alvos.get(e.target);
          /* Sai do observador junto com o aviso: o que já pediu os bytes não
             precisa ser recalculado no resto da rolagem. */
          alvos.delete(e.target);
          obs.unobserve(e.target);
          avisar?.();
        }
      },
      /* Uma tela de antecipação, só na vertical: a rolagem rápida encontra a
         mídia já pedida, e nada é buscado de lado — a faixa de miniaturas do
         visor é a única coisa que rola em X, e ela tem `loading="lazy"`. */
      { root: raiz, rootMargin: "400px 0px" },
    );
    reg = { obs, alvos };
    observadores.set(raiz, reg);
  }
  return reg;
}

export function esquecer(raiz, el) {
  const reg = observadores.get(raiz);
  if (!reg) return;
  reg.alvos.delete(el);
  reg.obs.unobserve(el);
  /* Painel fechado é container fora do DOM: sem soltar o registro vazio, o mapa
     seguraria o observador (e o container morto) até recarregar a página. */
  if (!reg.alvos.size) {
    reg.obs.disconnect();
    observadores.delete(raiz);
  }
}

/**
 * `true` quando o elemento chegou perto da tela — e nunca volta para `false`.
 *
 * Sem volta de propósito: descarregar o que saiu de vista faria a lista buscar o
 * mesmo áudio de novo a cada ida e volta da rolagem, e reiniciaria o player na
 * cara de quem estava ouvindo.
 */
function usePerto(imediato = false) {
  const alvo = useRef(null);
  /* Sem observador (navegador antigo, ambiente de teste) a prévia já nasce
     imediata: pior no peso, nunca em branco. */
  const [perto, setPerto] = useState(() => imediato || typeof IntersectionObserver === "undefined");

  useEffect(() => {
    const el = alvo.current;
    if (perto || !el) return undefined;
    const raiz = raizDeRolagem(el);
    const { obs, alvos } = paraObservar(raiz);
    alvos.set(el, () => setPerto(true));
    obs.observe(el);
    return () => esquecer(raiz, el);
  }, [perto]);

  return [alvo, perto];
}

/* ---------------------------------------------------------- player de voz */

const BARRAS = 40;
const VELOCIDADES = [1, 1.5, 2];

/* A tinta do player é a de quem o contém (`currentColor`), não uma cor fixa: o
   mesmo player toca dentro da bolha branca da cliente e da bolha preta do
   escritório, e cor fixa fica invisível numa das duas. */
const tinta = (pct) => `color-mix(in srgb, currentColor ${pct}%, transparent)`;

/**
 * Forma de onda determinística, semeada pelo nome do arquivo.
 *
 * Desenhar a onda de verdade exigiria baixar e decodificar cada áudio — catorze
 * por conversa, alguns de um mega. A onda semeada dá o mesmo desenho sempre para
 * o mesmo arquivo e serve ao que a onda faz aqui: mostrar progresso e onde
 * clicar. O envelope (seno) evita o cinquenta-por-cento chapado que denuncia
 * barra falsa.
 */
function onda(semente) {
  let h = 0;
  const t = String(semente ?? "audio");
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return Array.from({ length: BARRAS }, (_, i) => {
    h = (h * 1103515245 + 12345) >>> 0;
    const ruido = (h % 70) / 100;
    const envelope = 0.45 + 0.55 * Math.sin((i / BARRAS) * Math.PI);
    return Math.round(Math.max(0.18, ruido + 0.25) * envelope * 100);
  });
}

/**
 * O recado de voz, como no aplicativo: play redondo, onda clicável, tempo,
 * velocidade e download.
 *
 * O `<audio controls>` do navegador não tem velocidade — e o atendimento desta
 * casa é feito de áudio de dois minutos que alguém precisa ouvir a 2x para
 * despachar a fila. Por isso o controle é nosso.
 *
 * `imediato` é o visor: ali a pessoa PEDIU o arquivo, e esperar a interseção
 * seria fazer o clique dela render um quadro depois. Na lista, o padrão manda —
 * a onda já está desenhada (é semeada pelo nome) e a duração entra quando os
 * bytes chegam, do mesmo jeito que sempre entrou.
 */
/**
 * `duracao` é a duração CONHECIDA, em segundos, para quando o arquivo não a diz.
 *
 * O webm do MediaRecorder sai sem duração no cabeçalho: o player mostrava
 * `--:--` e, sem `total`, clicar na onda não movia a agulha — player que parece
 * quebrado numa reunião que está toda ali. Quem sabe o tempo é o registro da
 * gravação (`duracao_seg`), e é ele que entra aqui até o navegador descobrir
 * sozinho.
 */
export function PlayerDeVoz({ anexo, imediato = false, aoTempo, irPara, duracao = 0 }) {
  const [alvo, perto] = usePerto(imediato);
  const audio = useRef(null);
  const [tocando, setTocando] = useState(false);
  const [agora, setAgora] = useState(0);
  const [total, setTotal] = useState(Number(duracao) || 0);
  const [velocidade, setVelocidade] = useState(1);
  const [erro, setErro] = useState("");
  const barras = useMemo(() => onda(anexo.nome), [anexo.nome]);

  useEffect(() => {
    const el = audio.current;
    if (!el) return undefined;
    const noTempo = () => {
      setAgora(el.currentTime || 0);
      /* Quem está de fora acompanha o tempo (a transcrição da reunião acende a
         fala do momento). Callback e não estado compartilhado: o player continua
         funcionando sozinho em toda conversa da mesa, sem contexto novo. */
      aoTempo?.(el.currentTime || 0);
    };
    /* `Infinity` é o que o webm sem cabeçalho de duração devolve — e aceitá-lo
       apagaria a duração que o registro já sabia. */
    const naDuracao = () => Number.isFinite(el.duration) && el.duration > 0 && setTotal(el.duration);
    const noFim = () => {
      setTocando(false);
      setAgora(0);
    };
    el.addEventListener("timeupdate", noTempo);
    el.addEventListener("loadedmetadata", naDuracao);
    el.addEventListener("durationchange", naDuracao);
    const noErro = () => setErro("Este áudio não toca aqui — o arquivo pode estar incompleto.");
    const naPausa = () => setTocando(false);
    el.addEventListener("ended", noFim);
    el.addEventListener("error", noErro);
    el.addEventListener("pause", naPausa);
    return () => {
      el.removeEventListener("timeupdate", noTempo);
      el.removeEventListener("loadedmetadata", naDuracao);
      el.removeEventListener("durationchange", naDuracao);
      el.removeEventListener("ended", noFim);
      el.removeEventListener("error", noErro);
      el.removeEventListener("pause", naPausa);
    };
  }, [anexo.url, aoTempo]);

  /*
   * Tocar, com a falha na cara.
   *
   * A recusa do `play()` era engolida e o botão simplesmente não fazia nada — foi
   * assim que um recado de voz servido com o tipo errado passou por "player
   * quebrado" sem uma palavra de explicação.
   *
   * E toca um de cada vez, como no aplicativo: começar um pausa os outros, senão
   * catorze recados na mesma conversa viram catorze vozes ao mesmo tempo.
   */
  const tocar = () => {
    const el = audio.current;
    if (!el) return;
    /* Cinto: entre o player aparecer e o observador avisar cabe um quadro, e num
       navegador com a linha principal ocupada (foi o sintoma que trouxe a prévia
       sob demanda) cabe mais. Clique em play sempre toca. */
    if (!el.getAttribute("src")) el.src = anexo.url;
    for (const outro of document.querySelectorAll("audio")) if (outro !== el) outro.pause();
    setErro("");
    el.playbackRate = velocidade;
    el.play().then(
      () => setTocando(true),
      (e) =>
        setErro(
          e?.name === "NotAllowedError"
            ? "O navegador bloqueou o som — clique na página e tente de novo."
            : "Este áudio não toca aqui.",
        ),
    );
  };

  const alternar = () => {
    const el = audio.current;
    if (!el) return;
    if (tocando) return el.pause();
    tocar();
  };

  /*
   * Pular para um segundo, PEDIDO DE FORA: clicar numa fala da transcrição.
   *
   * A ordem chega como valor (`{ segundo, n }`) e não como método num `ref`:
   * o player continua dono do `<audio>` — duplicar "tempo atual" em dois lugares
   * é como nasce player que discorda de si mesmo. O `n` existe porque clicar
   * DUAS vezes na mesma fala é um pedido novo, e um número igual não dispararia
   * o efeito.
   */
  useEffect(() => {
    if (!irPara) return;
    const el = audio.current;
    if (!el) return;
    if (!el.getAttribute("src")) el.src = anexo.url;
    el.currentTime = Math.max(0, Number(irPara.segundo) || 0);
    setAgora(el.currentTime);
    if (el.paused) tocar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só o pedido novo dispara
  }, [irPara]);

  const trocarVelocidade = () => {
    const proxima = VELOCIDADES[(VELOCIDADES.indexOf(velocidade) + 1) % VELOCIDADES.length];
    setVelocidade(proxima);
    if (audio.current) audio.current.playbackRate = proxima;
  };

  /* Clicar na onda também TOCA quando está parado: a onda é o alvo grande e óbvio
     do player, e só mover a agulha (sem som) é indistinguível de player quebrado. */
  const buscar = (e) => {
    const el = audio.current;
    if (!el || !total) return;
    const caixa = e.currentTarget.getBoundingClientRect();
    el.currentTime = Math.min(1, Math.max(0, (e.clientX - caixa.left) / caixa.width)) * total;
    setAgora(el.currentTime);
    if (el.paused) tocar();
  };

  const andado = total ? agora / total : 0;

  return (
    <div
      ref={alvo}
      className="flex min-w-[min(260px,100%)] items-center gap-2.5 rounded-cartao border border-current/12 bg-current/8 p-[8px_10px]"
    >
      {/* Sem `src` não há requisição nenhuma — e é a linha que tirou 10 MB de
          áudio da abertura do caso. Quando o anexo chega perto, o atributo entra
          e o `preload="metadata"` volta a fazer o que fazia. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audio} src={perto ? anexo.url : undefined} preload="metadata" />
      <Button
        variant="ghost"
        onClick={alternar}
        data-dica={tocando ? "Pausar" : "Ouvir"}
        aria-label={tocando ? "Pausar" : "Ouvir"}
        /* Fundo por CLASSE (`bg-current/…`) e não por estilo: o estilo inline
           vencia o hover e o play não reagia ao ponteiro; e o `ghost` do
           registro troca a letra por `foreground` no hover, que na bolha
           escura apagava o ícone. A letra fica a da bolha. */
        className="size-padrao flex-none rounded-full bg-current/18 p-0 text-corpo text-inherit transition-colors hover:bg-current/26 hover:text-inherit dark:hover:bg-current/26"
      >
        <Icone de={tocando ? ICONES.pausar : ICONES.tocar} tamanho={16} />
      </Button>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* 40 barras com mínimo de 2px e 2px de vão pediam 158px: no balão do
            celular a onda passava por baixo do "1x" e saía do balão (02/11). */}
        <div
          onClick={buscar}
          data-dica="Avançar ou voltar"
          className="flex h-[26px] cursor-pointer items-center gap-px overflow-hidden"
        >
          {barras.map((altura, i) => (
            <span
              key={i}
              className="min-w-px flex-1 rounded-[2px]"
              /* Altura da barra e cor da agulha: as duas saem da onda semeada e do
                 quanto já tocou — conta de execução, `style`. */
              style={{ height: `${altura}%`, background: i / BARRAS <= andado ? "currentColor" : tinta(26) }}
            />
          ))}
        </div>
        {erro ? <div className="text-apoio leading-[1.35] opacity-85">{erro}</div> : null}
        <div className="flex justify-between font-mono text-selo opacity-65">
          <span>{mmss(tocando || agora ? agora : total)}</span>
          {/* Sem duração ainda, `--:--`: 00:00 parecia áudio vazio. */}
          <span>{total ? mmss(total) : "--:--"}</span>
        </div>
      </div>

      <div className="flex flex-none flex-col items-center gap-[5px]">
        <Button
          variant="ghost"
          onClick={trocarVelocidade}
          data-dica="Velocidade"
          className="h-auto rounded-full bg-current/16 px-2 py-[3px] text-selo font-bold text-inherit transition-colors hover:bg-current/24 hover:text-inherit dark:hover:bg-current/24"
        >
          {velocidade}x
        </Button>
        <a
          href={anexo.url}
          download={anexo.nome}
          data-dica="Baixar áudio"
          aria-label="Baixar áudio"
          className="alvo flex rounded-etiqueta text-inherit no-underline opacity-50 transition-opacity hover:opacity-100"
        >
          <Icone de={ICONES.baixar} tamanho={14} />
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- visor (lightbox) */

/* Estado de módulo em vez de contexto: o visor é único na tela e quem abre são
   dois componentes de galhos diferentes da árvore. Um contexto só para isso
   obrigaria a envolver a casca inteira para nada. */
let visorAtual = { lista: [], acao: null };
const ouvintes = new Set();
const avisar = () => ouvintes.forEach((fn) => fn(visorAtual));

/**
 * Abre o visor com a lista inteira e o índice do que foi clicado.
 *
 * QUAL anexo está aberto vai para a URL (`?anexo=`), como as outras
 * sobreposições da mesa: o visor é tela cheia, e no celular o voltar do Android é
 * O gesto de fechar tela cheia — em estado de módulo ele saía da vista por baixo
 * do visor aberto. De carona, "abre no anexo tal" passou a ser link.
 *
 * A LISTA e a AÇÃO ficam no módulo: vêm da conversa ou do acervo, e `acao` é
 * função (marcar como prova) — nada disso caberia numa query.
 *
 * ABRIR EMPILHA no histórico, e é o único passo do visor que empilha: andar de
 * anexo em anexo substitui, e fechar DESEMPILHA (ver `fechar`). Com `ajustar`
 * substituindo também na abertura, o voltar do Android não fechava o visor — ele
 * tirava a pessoa da tela inteira, com o visor junto.
 * ponytail: por isso recarregar com `?anexo=2` fecha o visor e limpa o
 * parâmetro; endereçar o anexo de verdade pede a lista vir do servidor.
 */
export function useAbrirVisor() {
  const { ajustar } = useNavegacao();
  return useCallback(
    (lista, indice = 0, acao = null) => {
      visorAtual = { lista: (lista ?? []).filter((a) => a?.url), acao, empurrou: true };
      avisar();
      ajustar({ anexo: Math.max(0, indice) }, { substituir: false });
    },
    [ajustar],
  );
}

function useVisor() {
  const [estado, setEstado] = useState(visorAtual);
  useEffect(() => {
    ouvintes.add(setEstado);
    return () => ouvintes.delete(setEstado);
  }, []);
  return estado;
}

const BOTAO_VISOR = "size-toque flex-none rounded-controle border border-creme/28 bg-escuro/55 p-0 text-creme";
/* A navegação é o gesto principal do visor: alvo maior e traço no tamanho dos
   outros ícones da casa (20px), não uma setinha de texto. */
/* No telefone as setas saem: 52px de cada lado de uma tela de 412 é um quarto
   da largura tirado do documento. Lá se anda deslizando (ver `deslizar`) e
   pela faixa de miniaturas. */
const BOTAO_SETA = "hidden size-[52px] flex-none rounded-full border border-creme/22 bg-escuro/50 p-0 text-creme sm:flex";

/** O conteúdo grande, por tipo. Sem prévia possível, oferece o download. */
/**
 * A foto do visor com zoom — o gesto do lightbox do TeamMove (Laura, 15/10:
 * ler o documento fotografado sem baixar). A roda aproxima no ponto do cursor,
 * o duplo clique alterna 2,5× e arrastar passeia. A borda nunca entra na área
 * visível: passear além dela é perder a foto de vista.
 *
 * A caixa de fora NÃO se transforma — é ela que dá o centro e o tamanho de
 * repouso para a conta; quem escala é a `<img>` de dentro, e quem recorta é a
 * área do visor (`data-area-do-zoom`).
 */
const ZOOM_MAXIMO = 8;
const distancia = ([a, b]) => Math.hypot(a.x - b.x, a.y - b.y);
function ImagemComZoom({ anexo }) {
  const caixa = useRef(null);
  const arrasto = useRef(null);
  const [vista, setVista] = useState({ z: 1, x: 0, y: 0 });
  const [arrastando, setArrastando] = useState(false);

  const limitar = (x, y, z) => {
    const el = caixa.current;
    const area = el?.closest("[data-area-do-zoom]");
    if (!el || !area) return { x, y };
    const mx = Math.max(0, (el.offsetWidth * z - area.clientWidth) / 2);
    const my = Math.max(0, (el.offsetHeight * z - area.clientHeight) / 2);
    return { x: Math.min(mx, Math.max(-mx, x)), y: Math.min(my, Math.max(-my, y)) };
  };
  /* O ponto do cursor relativo ao centro: é ele que fica parado na tela. */
  const ancora = (e) => {
    const r = caixa.current.getBoundingClientRect();
    return { x: e.clientX - (r.left + r.width / 2), y: e.clientY - (r.top + r.height / 2) };
  };
  const zoomPara = (alvo, a) =>
    setVista((v) => {
      const z = Math.max(1, Math.min(alvo(v.z), ZOOM_MAXIMO));
      if (z === 1) return { z: 1, x: 0, y: 0 };
      const k = z / v.z;
      return { z, ...limitar(a.x - (a.x - v.x) * k, a.y - (a.y - v.y) * k, z) };
    });

  /* Ouvinte nativo, não `onWheel`: o React registra a roda como passiva, e sem
     `preventDefault` a página rola junto com o zoom. */
  useEffect(() => {
    const el = caixa.current;
    const roda = (e) => {
      e.preventDefault();
      const passo = e.deltaY > 0 ? 0.85 : 1.15;
      zoomPara((z) => z * passo, ancora(e));
    };
    el.addEventListener("wheel", roda, { passive: false });
    return () => el.removeEventListener("wheel", roda);
    /* oxlint-disable-next-line react/exhaustive-effect-dependencies -- `zoomPara` e `ancora` só leem ref e escrevem estado por função: ligar uma vez basta */
  }, []);

  /*
   * No dedo: pinça aproxima no meio dos dois dedos, e o toque duplo alterna como
   * o duplo clique. Os dois são conta nossa, não do navegador — `touch-none` é o
   * que impede a página inteira de ampliar junto, e com ele o navegador também
   * deixa de juntar dois toques em `dblclick`.
   */
  const dedos = useRef(new Map());
  const pinca = useRef(null);
  const ultimoToque = useRef({ t: 0, x: 0, y: 0 });
  const dedoEm = useRef(-Infinity);
  const alternar = (e) => (vista.z > 1 ? setVista({ z: 1, x: 0, y: 0 }) : zoomPara(() => 2.5, ancora(e)));

  const soltar = (e) => {
    dedos.current.delete(e.pointerId);
    arrasto.current = null;
    setArrastando(false);
    if (e.pointerType !== "touch") return;
    dedoEm.current = e.timeStamp;
    if (pinca.current) {
      if (dedos.current.size < 2) pinca.current = { ...pinca.current, acabou: true };
      if (!dedos.current.size) pinca.current = null;
      return;
    }
    const antes = ultimoToque.current;
    const agora = { t: e.timeStamp, x: e.clientX, y: e.clientY };
    if (agora.t - antes.t < 300 && Math.hypot(agora.x - antes.x, agora.y - antes.y) < 30) {
      alternar(e);
      ultimoToque.current = { t: 0, x: 0, y: 0 };
    } else ultimoToque.current = agora;
  };
  /* Com zoom (ou no meio de uma pinça), o toque é de passear: não pode subir
     para o deslizar do visor e trocar de foto no meio da leitura. */
  const prender = (e) => (vista.z > 1 || pinca.current) && e.stopPropagation();

  return (
    <div
      ref={caixa}
      onDoubleClick={(e) => {
        /* O toque duplo já foi contado em `soltar`; o `dblclick` que algum
           navegador ainda mande depois desfaria o zoom. */
        if (e.timeStamp - dedoEm.current > 600 && !dedos.current.size) alternar(e);
      }}
      onPointerDown={(e) => {
        if (e.pointerType === "touch") {
          dedos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (dedos.current.size === 2) {
            pinca.current = { d: distancia([...dedos.current.values()]), z: vista.z };
            arrasto.current = null;
            return;
          }
        }
        if (vista.z <= 1) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        arrasto.current = { px: e.clientX, py: e.clientY, x: vista.x, y: vista.y };
        setArrastando(true);
      }}
      onPointerMove={(e) => {
        if (dedos.current.has(e.pointerId)) dedos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const p = pinca.current;
        if (p && !p.acabou && dedos.current.size === 2) {
          const [a, b] = [...dedos.current.values()];
          const meio = { clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 };
          zoomPara(() => (p.z * distancia([a, b])) / p.d, ancora(meio));
          return;
        }
        const a = arrasto.current;
        if (a) setVista((v) => ({ ...v, ...limitar(a.x + e.clientX - a.px, a.y + e.clientY - a.py, v.z) }));
      }}
      onPointerUp={soltar}
      onPointerCancel={soltar}
      onTouchStart={prender}
      onTouchEnd={prender}
      className={`flex touch-none ${arrastando ? "cursor-grabbing" : vista.z > 1 ? "cursor-grab" : "cursor-zoom-in"}`}
    >
      <img
        src={anexo.url}
        alt={anexo.nome}
        draggable={false}
        style={{ transform: `translate(${vista.x}px, ${vista.y}px) scale(${vista.z})` }}
        className={`max-h-[78vh] max-w-full rounded-controle object-contain select-none ${arrastando ? "" : "transition-transform duration-100 ease-entra"}`}
      />
    </div>
  );
}

function Grande({ anexo }) {
  const familia = familiaDoAnexo(anexo.nome);
  const [naoToca, setNaoToca] = useState(false);
  const [soSom, setSoSom] = useState(false);

  /* `key`: trocar de foto volta ao tamanho de tela, como no TeamMove. */
  if (familia === "imagem") return <ImagemComZoom key={anexo.url} anexo={anexo} />;

  /* Formato que este navegador não decodifica (`.mov`, `.mkv`, `.3gp`) não pode
     virar tela preta: o arquivo está no servidor e o aparelho tem player. */
  if (familia === "video" && naoToca)
    return (
      <div className="w-[min(520px,92vw)] rounded-2xl bg-card p-5 text-foreground">
        <div className="mb-1.5 text-corpo font-semibold [overflow-wrap:anywhere]">{anexo.nome}</div>
        <div className="mb-3 text-corpo leading-[1.5] text-tinta-suave">
          Este navegador não toca esse formato de vídeo. Baixe para abrir no aparelho.
        </div>
        <a
          href={anexo.url}
          download={anexo.nome}
          className="inline-flex items-center gap-[7px] rounded-controle border border-borda-controle px-3 py-2 text-corpo font-semibold text-tinta-media no-underline hover:bg-muted"
        >
          <Icone de={ICONES.baixar} tamanho={14} /> Baixar vídeo
        </a>
      </div>
    );

  if (familia === "video")
    return (
      /* Sem `autoPlay`: o navegador bloqueia reprodução automática com som, e o
         vídeo ficava parado no primeiro quadro parecendo quebrado. Quem abriu o
         visor aperta o play — e o `Range` da rota é o que deixa ele começar. */
      <video
        src={anexo.url}
        controls
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => !e.currentTarget.videoWidth && setSoSom(true)}
        onError={() => setNaoToca(true)}
        /* Teto em `vh`, não em `%`: dentro de caixa `fit-content` a percentagem não
           resolve, e o vídeo em pé passava da dobra levando a barra de controle com
           ele — era o "vídeo travado num quadro", que na verdade era play fora da tela. */
        className="max-h-[72vh] max-w-full rounded-controle bg-black"
      />
    );

  /* Webm de áudio (o recado gravado na mesa) abria como vídeo: tela preta com
     barra de controle. Sem faixa de imagem, é o player de voz que serve. */
  if (familia === "audio" || soSom)
    return (
      <div className="w-[min(520px,92vw)] rounded-2xl bg-card p-5 text-foreground">
        <div className="mb-3 text-corpo font-semibold [overflow-wrap:anywhere]">{anexo.nome}</div>
        <PlayerDeVoz anexo={anexo} imediato />
      </div>
    );

  if (familia === "contato")
    return (
      <div className="w-[min(420px,92vw)] rounded-2xl bg-card p-5 text-foreground">
        <CartaoDeContato anexo={anexo} largura={380} />
      </div>
    );

  if (familia === "pdf")
    return (
      /*
       * Medida em `vw`/`vh`, não em `%`: a caixa que abraça a mídia é
       * `fit-content`, e `width:100%` dentro dela é circular — o quadro caía no
       * tamanho intrínseco do iframe (300×150) e o documento aparecia minúsculo no
       * meio da tela. Aqui o visor dá ao PDF o tamanho de leitura.
       */
      <LeitorDePdf url={anexo.url} nome={anexo.nome} className="h-[80vh] w-[min(920px,92vw)] rounded-controle" />
    );

  return (
    <div className="w-[min(420px,92vw)] rounded-2xl bg-card p-[26px] text-center text-foreground">
      <div className="mb-1.5 text-corpo font-semibold [overflow-wrap:anywhere]">{anexo.nome}</div>
      <div className="mb-4 text-apoio text-muted-foreground">Este formato não abre aqui.</div>
      <a
        href={anexo.url}
        download={anexo.nome}
        className="inline-block rounded-controle bg-tinta px-4 py-[9px] text-corpo font-bold text-mesa hover:bg-tinta/86 no-underline"
      >
        Baixar arquivo
      </a>
    </div>
  );
}

/**
 * O botão de ação do visor. Guarda o "já fiz" localmente: quem clicou precisa de
 * resposta na hora, e o estado que decide o rótulo mora na tela que abriu.
 */
function AcaoDoVisor({ acao, anexo }) {
  const [feito, setFeito] = useState(false);
  const jaEra = feito || acao.marcado?.(anexo);
  return (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        acao.aoAgir(anexo);
        setFeito(true);
      }}
      data-dica={acao.titulo}
      className={`h-toque flex-none gap-[5px] rounded-controle border px-[13px] text-selo font-bold tracking-[.03em] whitespace-nowrap text-creme ${
        jaEra ? "border-verde-vivo/55 bg-verde-vivo/22" : "border-creme/28 bg-escuro/55"
      }`}
    >
      {jaEra ? (
        <>
          <Icone de={ICONES.certo} tamanho={13} /> PROVA
        </>
      ) : (
        acao.rotulo
      )}
    </Button>
  );
}

/** O visor. Um por tela: montado na casca, aberto por `useAbrirVisor()`. */
export function Visor() {
  const { lista, acao } = useVisor();
  const { anexoAberto, ajustar } = useNavegacao();
  const indice = anexoAberto == null ? -1 : Number(anexoAberto);
  const anexo = lista[indice];
  const navegar = useNavigate();
  const irPara = (i) => ajustar({ anexo: ((i % lista.length) + lista.length) % lista.length });
  /* Fechar desfaz a entrada que abrir empilhou — senão cada visor aberto deixava
     no histórico um degrau igual ao de baixo, e o voltar parecia não fazer nada.
     Sem entrada nossa (link colado, recarregamento), só tira o parâmetro. */
  const fechar = () => {
    if (visorAtual.empurrou) {
      visorAtual = { ...visorAtual, empurrou: false };
      navegar(-1);
    } else ajustar({ anexo: null });
  };
  /* Fechado pelo voltar do navegador, a entrada já saiu: a próxima `fechar` não
     pode voltar de novo, ou levaria a pessoa uma tela para trás. */
  useEffect(() => {
    if (anexoAberto == null) visorAtual.empurrou = false;
  }, [anexoAberto]);

  /* Parâmetro sem lista é lixo de recarregamento (a lista mora em módulo): sai da
     URL sozinho, para o endereço não prometer um visor que não abre. */
  useEffect(() => {
    if (anexoAberto != null && !lista.length) ajustar({ anexo: null });
  }, [ajustar, anexoAberto, lista.length]);

  /* A miniatura acesa entra na faixa: com trinta arquivos a seta andava para um
     que estava fora da vista, e a faixa não dizia onde a pessoa estava. */
  const faixa = useRef(null);
  const inicioDoToque = useRef(null);
  const caixa = useRef(null);
  useEffect(() => {
    faixa.current
      ?.querySelector('[aria-current="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [indice]);

  if (!anexo) return null;

  /*
   * As setas moram NA CAIXA, não na janela — e numa caixa NOSSA, dentro do
   * `Popup`.
   *
   * O ouvinte em `window` nunca disparava: a tecla nasce dentro do `Dialog` (o
   * foco fica preso nele) e um manipulador do React no caminho para a raiz
   * parava a propagação antes de ela chegar lá — o visor anunciava a seta e a
   * seta não andava. E o `onKeyDown` no próprio `Popup` do Base UI fica com a
   * função da PRIMEIRA montagem: a seta para a direita andava, a para a esquerda
   * voltava a partir do índice de quando o visor abriu. O Esc continua do `Dialog`, que também devolve o foco ao
   * cartão de onde o visor abriu.
   *
   * O vídeo e o áudio usam as setas para avançar no tempo: com o foco neles, a
   * tecla é deles.
   *
   * E a navegação continua sendo nossa, não de um `Carousel`: qual anexo está
   * aberto mora na URL (`?anexo=`), e um carrossel com índice próprio brigaria
   * com o botão voltar do navegador — que é o gesto de fechar tela cheia.
   */
  const teclar = (e) => {
    if (e.target.closest?.("video, audio, input, textarea")) return;
    /* O índice do ENDEREÇO, não o da renderização: tecla segurada (ou duas
       seguidas) chega antes de o React redesenhar, e a conta sobre o índice
       velho fazia a seta para a esquerda pular para o fim da lista. */
    const agora = Number(new URLSearchParams(window.location.search).get("anexo") ?? indice);
    const destino = { ArrowRight: agora + 1, ArrowLeft: agora - 1, Home: 0, End: lista.length - 1 }[e.key];
    if (destino === undefined || lista.length < 2) return;
    e.preventDefault();
    irPara(destino);
  };

  /* Deslizar para o lado troca de anexo, como a galeria do telefone. Só o gesto
     francamente horizontal: a rolagem vertical do PDF e o arraste do zoom não
     podem virar troca de arquivo no meio da leitura. */
  const deslizar = {
    onTouchStart: (e) => {
      const t = e.touches[0];
      inicioDoToque.current = e.touches.length === 1 ? { x: t.clientX, y: t.clientY } : null;
    },
    onTouchEnd: (e) => {
      const i = inicioDoToque.current;
      inicioDoToque.current = null;
      if (!i || lista.length < 2) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - i.x;
      if (Math.abs(dx) > 60 && Math.abs(dx) > 2 * Math.abs(t.clientY - i.y)) irPara(indice + (dx < 0 ? 1 : -1));
    },
  };

  /* Vidro é o que NÃO é controle nem mídia: tocar nele fecha, como todo visor.
     Era o clique subindo de qualquer filho — inclusive das setas, e "Próximo"
     trocava de anexo e fechava o visor no mesmo toque. */
  const tocarNoVidro = (e) => {
    if (!e.target.closest?.("button, a, [data-segura]")) fechar();
  };

  return (
    <CortinaLivre
      aoFechar={fechar}
      aoTocar={tocarNoVidro}
      focoInicial={caixa}
      rotulo={`Anexo: ${anexo.nome}`}
      z={80}
      fundo="color-mix(in oklab, var(--color-visor) 94%, transparent);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)"
      moldura="position:fixed;inset:0"
      estilo="position:absolute;inset:0;display:flex;flex-direction:column;animation:vFade var(--t-curta) var(--e-entra) both"
    >
      <div ref={caixa} tabIndex={-1} onKeyDown={teclar} className="flex min-h-0 flex-1 flex-col outline-none">
        <div className="flex flex-none items-center gap-3 p-[12px_14px] text-creme">
          <div data-segura className="min-w-0 flex-1">
            <div className="overflow-hidden text-corpo font-semibold text-ellipsis whitespace-nowrap">{anexo.nome}</div>
            <div className="mt-0.5 font-mono text-selo text-creme/60">
              {indice + 1} de {lista.length}
              {anexo.quando ? ` · ${dataHora(anexo.quando)}` : ""}
            </div>
          </div>
          {acao && <AcaoDoVisor acao={acao} anexo={anexo} />}
          {/* O leitor da aba tem busca, zoom e impressão — o quadro aqui dentro é
            para ler de relance, e quem precisa procurar uma cláusula vai lá. */}
          {familiaDoAnexo(anexo.nome) === "pdf" ? (
            <a
              href={anexo.url}
              target="_blank"
              rel="noopener"
              data-dica="Abrir em outra aba"
              className="hidden h-toque flex-none items-center justify-center rounded-controle border border-creme/28 bg-escuro/55 px-3 text-selo font-bold tracking-[.03em] whitespace-nowrap text-creme no-underline sm:flex"
            >
              ABRIR EM ABA
            </a>
          ) : null}
          <a
            href={anexo.url}
            download={anexo.nome}
            data-dica="Baixar"
            aria-label="Baixar"
            className={`flex items-center justify-center no-underline ${BOTAO_VISOR}`}
          >
            <Icone de={ICONES.baixar} tamanho={18} />
          </a>
          <Fechar
            aoTocar={fechar}
            titulo="Fechar o visor"
            lado={40}
            tom="color-mix(in oklab, var(--color-foreground) 55%, transparent)"
            letra="var(--color-creme)"
          />
        </div>

        {/* Sem `stopPropagation` na faixa inteira: o vidro ao lado da mídia é área
          de fechar (é o gesto que todo visor tem). Quem segura o clique é só a
          mídia e os botões. */}
        <div {...deslizar} className="flex min-h-0 flex-1 items-center justify-center gap-3.5 p-[0_14px_14px]">
          {lista.length > 1 && (
            <Button onClick={() => irPara(indice - 1)} data-dica="Anterior" aria-label="Anterior" className={BOTAO_SETA}>
              <Icone de={ICONES.voltar} tamanho={24} />
            </Button>
          )}
          <div data-area-do-zoom className="flex h-full min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden">
            {/* A caixa que segura o clique abraça a mídia (`fit-content`): o vazio
              em volta é vidro, e vidro é para fechar. */}
            <div data-segura className="flex max-h-full w-fit max-w-full items-center justify-center">
              <Grande anexo={anexo} />
            </div>
          </div>
          {lista.length > 1 && (
            <Button onClick={() => irPara(indice + 1)} data-dica="Próximo" aria-label="Próximo" className={BOTAO_SETA}>
              <Icone de={ICONES.avancar} tamanho={24} />
            </Button>
          )}
        </div>

        {lista.length > 1 && (
          /* A faixa rola, e o centro é do conteúdo (`mx-auto` num filho `w-max`), não
           da faixa: `justify-center` num contêiner que transborda empurra o
           começo para fora da borda esquerda, onde a rolagem não alcança — com
           trinta anexos, os primeiros ficavam impossíveis de escolher. */
          <div ref={faixa} data-segura className="flex-none overflow-x-auto p-[0_14px_16px]">
            <div className="mx-auto flex w-max gap-[7px]">
              {lista.map((a, i) => (
                <Button
                  key={a.url + i}
                  onClick={() => irPara(i)}
                  data-dica={a.nome}
                  aria-label={a.nome}
                  aria-current={i === indice ? "true" : undefined}
                  className={`size-[46px] flex-none overflow-hidden rounded-controle border-2 bg-creme/10 p-0 text-selo font-bold text-creme ${
                    i === indice ? "border-creme" : "border-transparent"
                  }`}
                >
                  {familiaDoAnexo(a.nome) === "imagem" ? (
                    /* A faixa desenha a lista INTEIRA, e cada miniatura de 46px é o
                   arquivo original: abrir o visor de um caso com vinte fotos
                   baixava as vinte em tamanho de câmera para caber num quadrado.
                   `lazy` limita ao que está na faixa visível.
                   ponytail: continua sendo o arquivo cheio — miniatura de
                   verdade é a rota servir um redimensionado. */
                    <img src={a.url} alt="" loading="lazy" decoding="async" className="block size-full object-cover" />
                  ) : (
                    EXTENSAO(a.nome).slice(0, 4).toUpperCase()
                  )}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </CortinaLivre>
  );
}

/* ------------------------------------------------------------- na conversa */

/**
 * O vídeo na conversa: cartaz com play, duração e o primeiro quadro — não um
 * player aberto.
 *
 * `<video controls>` solto na bolha dava três problemas: barra preta do navegador
 * dentro do desenho, quadro em branco até alguém apertar play e um vídeo de 13 MB
 * começando a carregar em cada mensagem só por existir na tela. Aqui é cartaz; o
 * vídeo toca no visor, que é onde ele tem tamanho.
 *
 * Div com papel de botão, e não `<button>`: `<video>` dentro de botão é HTML
 * inválido, e HTML inválido é onde o navegador começa a inventar.
 */
function PreviaDeVideo({ anexo, moldura, raio, aoAbrir, escuro }) {
  const [alvo, perto] = usePerto();
  const [duracao, setDuracao] = useState(0);
  const [falhou, setFalhou] = useState(false);
  const [soSom, setSoSom] = useState(false);

  /* `.webm` é contêiner: o recado gravado aqui na mesa é webm SEM imagem, e como
     "vídeo" ele virava um retângulo preto que não tocava. Quem decide é o
     arquivo, não a extensão — sem faixa de imagem (`videoWidth` zero), é áudio. */
  if (soSom) return <PlayerDeVoz anexo={anexo} />;

  /* `.mov` do iPhone e `.3gp` do Android o Chrome às vezes não decodifica. Antes
     isso era um retângulo preto sem explicação; agora vira cartão que abre o
     arquivo no aparelho, onde o player do sistema dá conta. */
  if (falhou) return <Cartao anexo={anexo} escuro={escuro} aoAbrir={() => window.open(anexo.url, "_blank", "noopener")} />;

  return (
    <div
      ref={alvo}
      role="button"
      tabIndex={0}
      onClick={aoAbrir}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && aoAbrir()}
      aria-label={anexo.nome}
      className="relative w-full cursor-pointer overflow-hidden border bg-visor leading-none"
      style={{ borderColor: moldura, borderRadius: raio }}
    >
      {/* O elemento fica montado sem `src` — é o que mantém o cartaz do mesmo
          tamanho antes e depois (vídeo vazio mede 300×150, a mesma caixa preta
          que o quadro preto de abertura desenhava) e não gasta um byte. O
          `preload="metadata"` deste anexo custava até 13 MB: eram três vídeos da
          Angélica, 20 MB, buscados por existirem na conversa.

          Com `src`, o de sempre: `metadata` traz o primeiro quadro e um segundo
          dentro do vídeo evita o quadro preto de abertura. */}
      <video
        src={perto ? `${anexo.url}#t=1` : undefined}
        preload="metadata"
        muted
        playsInline
        onLoadedMetadata={(e) => {
          if (!e.currentTarget.videoWidth) return setSoSom(true);
          setDuracao(e.currentTarget.duration || 0);
        }}
        onError={() => setFalhou(true)}
        /* `contain` e não `cover`: vídeo de celular é retrato, e cortar as laterais
           tirava justamente quem está falando. O fundo preto faz as bordas. */
        className="block max-h-[340px] w-full bg-visor object-contain"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-visor/20">
        <span className="flex size-12 items-center justify-center rounded-full bg-creme/92 text-escuro shadow-[0_4px_14px_color-mix(in_oklab,_var(--color-visor)_35%,_transparent)]">
          <Icone de={ICONES.tocar} tamanho={20} />
        </span>
      </span>
      <Badge className="absolute right-2 bottom-[7px] h-auto rounded-etiqueta bg-visor/72 px-1.5 py-0.5 font-mono text-apoio leading-[1.4] font-normal text-creme">
        {duracao ? mmss(duracao) : "vídeo"}
      </Badge>
    </div>
  );
}

/* ------------------------------------------------------------------ contato */

/**
 * O cartão de contato (.vcf) lido de verdade.
 *
 * Busca o arquivo (são 400 bytes) para mostrar nome e telefone: o conteúdo do
 * anexo É a informação, e como "arquivo desconhecido" o telefone ficava escondido
 * dentro dele. Falhou a leitura, volta a ser cartão de arquivo — sem inventar.
 */
function CartaoDeContato({ anexo, escuro, largura }) {
  const [cartao, setCartao] = useState(undefined);
  const { abrirDoContato } = useAcoes();
  const pode = usePode();

  useEffect(() => {
    let cancelado = false;
    fetch(anexo.url, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => !cancelado && setCartao(lerVCard(t)))
      .catch(() => !cancelado && setCartao(null));
    return () => {
      cancelado = true;
    };
  }, [anexo.url]);

  /* Enquanto os 400 bytes não chegam, o cartão de arquivo com um `Spinner` no
     lugar da extensão: era a sigla "VCF" parada, indistinguível de leitura que
     falhou — e falha é o outro ramo, que não gira. */
  if (cartao === undefined || cartao === null) return <Cartao anexo={anexo} escuro={escuro} carregando={cartao === undefined} />;

  const moldura = escuro
    ? "color-mix(in oklab, var(--color-creme) 18%, transparent)"
    : "color-mix(in oklab, var(--color-foreground) 9%, transparent)";
  const fundo = escuro
    ? "color-mix(in oklab, var(--color-creme) 8%, transparent)"
    : "color-mix(in oklab, var(--color-foreground) 3%, transparent)";
  return (
    <div
      className="w-full rounded-xl border p-[11px_12px]"
      style={{ maxWidth: `${largura}px`, borderColor: moldura, background: fundo }}
    >
      <div className="flex items-center gap-2.5">
        <Rosto nome={cartao.nome} tamanho={38} />
        <div className="min-w-0 flex-1">
          <div className="text-corpo font-semibold [overflow-wrap:anywhere]">{cartao.nome}</div>
          {(cartao.organizacao || cartao.cargo) && (
            <div className="mt-px text-selo opacity-70 [overflow-wrap:anywhere]">
              {[cartao.cargo, cartao.organizacao].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      </div>

      {cartao.telefones.map((t) => (
        <a
          key={t.valor}
          href={`tel:${t.valor.replace(/[^+\d]/g, "")}`}
          onClick={(e) => e.stopPropagation()}
          className="mt-2 flex justify-between gap-2.5 font-mono text-corpo font-semibold text-inherit no-underline"
        >
          <span className="font-sans text-selo lowercase opacity-60">{t.tipo}</span>
          <span>{t.valor}</span>
        </a>
      ))}
      {cartao.emails.map((e) => (
        <div key={e.valor} className="mt-1.5 text-apoio opacity-80 [overflow-wrap:anywhere]">
          {e.valor}
        </div>
      ))}
      {cartao.enderecos[0] && <div className="mt-1.5 text-apoio leading-[1.45] opacity-65">{cartao.enderecos[0]}</div>}

      {/* "Salvar contato" baixava o .vcf, que no computador não serve para nada
          (Laura, 15/10). O que a pessoa quer é falar com quem foi indicado: abre
          o caso desse número, ou cria um com o nome do cartão. */}
      {cartao.telefones[0] && pode("registros:criar") ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            abrirDoContato({ nome: cartao.nome ?? "", telefone: cartao.telefones[0].valor });
          }}
          className="mt-2.5 block w-full cursor-pointer border-0 border-t border-solid bg-transparent pt-[9px] text-left text-selo font-bold tracking-[.04em] text-inherit opacity-70 hover:opacity-100"
          style={{ borderTopColor: moldura }}
        >
          ABRIR ATENDIMENTO
        </button>
      ) : null}
    </div>
  );
}

/**
 * A prévia dentro da bolha (e no painel de anexos).
 *
 * Ocupa a largura que a bolha der (`width:100%`, com teto em `largura`): é a
 * mídia que define o tamanho da bolha, e não o contrário — antes o áudio de
 * 260px ficava perdido numa bolha de 70% da tela.
 *
 * Áudio e vídeo não abrem o visor para tocar: o atendimento é feito de recado de
 * voz, e obrigar um clique a mais em cada um dos catorze áudios de um caso é o
 * mesmo que não ter conversa na tela. Imagem, PDF e o resto abrem no visor.
 *
 * `encaixar` é a foto dentro da grade da galeria: a célula manda a forma, então a
 * imagem preenche e o canto arredondado sai (quem arredonda é a grade inteira).
 */
/**
 * `leve` serve a lista de arquivos, que é outro problema da conversa.
 *
 * Na conversa há poucos anexos por vez e a prévia É a mensagem — vale buscar
 * bytes. Numa pasta com 26 arquivos, não: medido no navegador, a grade baixava
 * 29,04 MB antes de a pessoa clicar em nada, e o pior era o PDF, cujo `<iframe>`
 * puxa o arquivo INTEIRO para desenhar a primeira página. No modo leve só a
 * imagem (que é a miniatura útil) traz bytes; o resto mostra o cartão com nome,
 * tipo e tamanho, e abre no visor quando a pessoa pede.
 */
export function PreviaDeAnexo({
  anexo,
  lista,
  acao,
  escuro = false,
  largura = 320,
  encaixar = false,
  raio = "12px",
  leve = false,
  /* Figurinha: sem moldura e sem fundo, porque ela tem transparência e recorte
     próprios — o quadrado cinza em volta é justamente o que o WhatsApp não põe. */
  limpo = false,
}) {
  const familia = familiaDoAnexo(anexo.nome);
  const abrirVisor = useAbrirVisor();
  const abrir = () => {
    const todos = lista ?? [anexo];
    abrirVisor(
      todos,
      todos.findIndex((a) => a.url === anexo.url),
      acao,
    );
  };
  const moldura = limpo
    ? "transparent"
    : escuro
      ? "color-mix(in oklab, var(--color-creme) 18%, transparent)"
      : "color-mix(in oklab, var(--color-foreground) 9%, transparent)";
  const fundo = limpo
    ? "transparent"
    : escuro
      ? "color-mix(in oklab, var(--color-creme) 10%, transparent)"
      : "color-mix(in oklab, var(--color-foreground) 4%, transparent)";
  /* `largura` é prop pública em pixel: teto continua no `style`. */
  const teto = encaixar ? undefined : `${largura}px`;

  if (!anexo.url) return <Cartao anexo={anexo} escuro={escuro} />;

  /* Modo leve: só imagem busca bytes. Vídeo, áudio, PDF e o resto viram cartão
     clicável — o visor carrega na hora em que a pessoa pede. */
  /* O `Cartao` JÁ é um botão: envolvê-lo em outro é `<button>` dentro de
     `<button>`, que o React acusa como erro de hidratação e que deixa o clique
     do teclado ambíguo. Quem abre é o cartão. */
  if (leve && familia !== "imagem")
    return (
      <div className="w-full" style={{ maxWidth: teto }}>
        <Cartao anexo={anexo} escuro={escuro} aoAbrir={abrir} />
      </div>
    );

  if (familia === "imagem")
    return (
      <button
        onClick={abrir}
        aria-label={anexo.nome}
        className={`block w-full cursor-zoom-in overflow-hidden p-0 leading-none ${
          encaixar || limpo ? "border-none" : "border"
        } ${encaixar ? "h-full" : ""}`}
        style={{
          background: fundo,
          borderColor: encaixar || limpo ? undefined : moldura,
          borderRadius: encaixar ? 0 : raio,
          maxWidth: teto,
        }}
      >
        {/*
          A foto fica com o `loading="lazy"` da plataforma, sem observador nosso.
          Medido no acervo da Angélica: as vinte e seis peças somam 29 MB e as
          fotos são 0,6 MB delas — a maior tem 115 KB. Apertar a margem aqui
          economizaria quilobyte e traria de volta o pulo de altura na coluna
          (foto sem bytes não tem altura), que é caro na masonry de Documentos.
          `decoding="async"` só tira a decodificação da linha principal.
          ponytail: sem teto de tamanho porque não existe miniatura no servidor —
          se um cliente mandar foto de 8 MB, o caminho é a rota gerar o
          redimensionado, não a tela baixar e encolher.
        */}
        <img
          src={anexo.url}
          alt={anexo.nome}
          loading="lazy"
          decoding="async"
          /* A CAIXA nasce com a altura final (4:5, teto de 320px), antes de a
             foto chegar: sem ela a foto crescia depois da pintura e empurrava a
             conversa — quem abria no fim caía no meio (29/10). O recorte é o da
             miniatura do WhatsApp; a foto inteira está a um toque, no visor. A
             figurinha fica de fora: é pequena e o recorte dela é o desenho. */
          className={`block w-full ${encaixar ? "h-full" : limpo ? "max-h-[320px]" : "aspect-[4/5] max-h-[320px]"} ${limpo ? "object-contain" : "object-cover"}`}
        />
      </button>
    );

  if (familia === "video")
    return (
      <div className="w-full" style={{ maxWidth: teto }}>
        <PreviaDeVideo anexo={anexo} moldura={moldura} raio={raio} aoAbrir={abrir} escuro={escuro} />
      </div>
    );

  if (familia === "audio")
    return (
      <div className="w-full min-w-[min(240px,100%)]" style={{ maxWidth: teto }}>
        <PlayerDeVoz anexo={anexo} />
      </div>
    );

  if (familia === "contato") return <CartaoDeContato anexo={anexo} escuro={escuro} largura={largura} />;

  if (familia === "pdf") return <PreviaDePdf anexo={anexo} moldura={moldura} raio={raio} teto={teto} aoAbrir={abrir} />;

  return <Cartao anexo={anexo} escuro={escuro} aoAbrir={abrir} />;
}

/**
 * O PDF na lista: selo primeiro, primeira página a pedido.
 *
 * O `<iframe>` que mostra a página baixa o DOCUMENTO INTEIRO — não a primeira
 * folha, o arquivo. Um por linha, isso é o acervo todo descendo por existir na
 * tela; e o do caso da Angélica tem 122 KB só porque é a CTPS digital, o
 * processo trabalhista vive de anexo de 20 MB. Aqui o quadro é uma escolha:
 * "ver a primeira página" quando reconhecer o documento importa, o clique no
 * cartão quando o assunto é ler — e ler sempre foi no visor.
 */
function PreviaDePdf({ anexo, moldura, raio, teto, aoAbrir }) {
  const [verPagina, setVerPagina] = useState(false);

  return (
    <div
      className="w-full overflow-hidden border bg-card text-left"
      style={{ maxWidth: teto, borderColor: moldura, borderRadius: raio }}
    >
      <div className="relative h-[230px] bg-card">
        {/*
          O selo é o estado normal do PDF na lista, não mais o forro branco do
          quadro: ele diz o que o arquivo é e o que o clique faz sem pedir um
          byte ao servidor. Botão de verdade, e não `div role="button"` como
          antes: sem o `<iframe>` dentro (ele só entra depois, como IRMÃO) não há
          mais HTML inválido para contornar, e sai de graça o Enter/Espaço do
          navegador.
        */}
        <Button
          variant="ghost"
          onClick={aoAbrir}
          data-dica={anexo.nome}
          aria-label={`Abrir ${anexo.nome}`}
          className="absolute inset-0 size-full flex-col gap-2 rounded-none bg-transparent p-0 text-muted-foreground"
        >
          <Icone de={ICONES.documento} tamanho={26} />
          <span className="text-selo font-bold tracking-[.08em]">PDF · CLIQUE PARA ABRIR</span>
        </Button>

        {/* Irmão do botão, e depois dele: fica por cima e recebe o próprio
            clique, sem controle dentro de controle. */}
        {!verPagina && (
          <Button
            variant="outline"
            onClick={() => setVerPagina(true)}
            className="absolute bottom-3.5 left-1/2 h-auto -translate-x-1/2 rounded-lg border bg-card px-[11px] py-[5px] text-selo font-bold whitespace-nowrap text-tinta-suave"
            style={{ borderColor: moldura }}
          >
            Ver a primeira página
          </Button>
        )}

        {verPagina && (
          <LeitorDePdf
            /* `view=Fit`: a página INTEIRA dentro do quadro. Prévia é para
               reconhecer o documento, não para ler — e qualquer zoom maior que o
               quadro traz barra de rolagem para dentro da bolha, que é o que a
               prévia não pode ter. Ler é no visor.

               `<embed>` não serve de troca: no Chromium sem o plugin de PDF ele
               escreve "Couldn't load plugin" por cima. O `#` esconde a barra do
               leitor, que num quadro de 230px só tapava o documento, e o quadro
               não recebe ponteiro — atrás dele continua o botão que abre o
               visor. */
            url={anexo.url}
            nome={anexo.nome}
            primeira
            parametros="toolbar=0&navpanes=0&scrollbar=0&page=1&view=Fit"
            className="pointer-events-none absolute inset-0 size-full"
          />
        )}
      </div>
      <div
        className="flex items-center gap-[7px] border-t p-[7px_10px] text-apoio font-semibold text-foreground"
        style={{ borderTopColor: moldura }}
      >
        <Icone de={ICONES.documento} tamanho={14} cor="var(--color-terra-escura)" />
        <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{anexo.nome}</span>
      </div>
    </div>
  );
}

/**
 * O cartão de sempre, para o que não tem prévia.
 *
 * `Attachment` do shadcn: o anexo tem slots com papel dito (mídia, título,
 * descrição, gatilho), e o gatilho é um `<button>` que cobre o cartão inteiro —
 * o alvo grande que o cartão feito à mão tinha, sem precisar que o cartão
 * INTEIRO seja um botão. Sem `aoAbrir` não nasce gatilho nenhum: o cartão do
 * arquivo que não está no servidor não é clicável, e um botão desabilitado
 * prometia o contrário.
 *
 * A paleta continua sendo a da casa, inclusive a pele escura (`escuro`): este
 * cartão também mora dentro da bolha preta do escritório, onde o `bg-card` do
 * preset seria um retângulo branco.
 */
function Cartao({ anexo, escuro, aoAbrir, carregando = false }) {
  return (
    <Attachment
      state={carregando ? "processing" : "done"}
      data-dica={anexo.nome}
      className={`w-full rounded-controle text-inherit ${escuro ? "border-creme/18 bg-creme/10" : "border-border bg-foreground/4"}`}
    >
      <AttachmentMedia
        className={`size-[34px] rounded-controle text-selo font-bold ${
          escuro ? "bg-creme/16 text-creme" : "bg-terra/14 text-terra-escura"
        }`}
      >
        {carregando ? <Spinner /> : EXTENSAO(anexo.nome).slice(0, 4).toUpperCase() || "?"}
      </AttachmentMedia>
      <AttachmentContent>
        <AttachmentTitle className="text-corpo font-semibold">{anexo.nome}</AttachmentTitle>
        <AttachmentDescription className="mt-px font-mono text-selo text-inherit opacity-65">
          {anexo.url ? "clique para abrir" : "arquivo não está no servidor"}
        </AttachmentDescription>
      </AttachmentContent>
      {aoAbrir ? <AttachmentTrigger onClick={aoAbrir} aria-label={anexo.nome} /> : null}
    </Attachment>
  );
}
